import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { abbreviateNumber } from 'app/utils/numbers';
import { truncateAddress } from 'app/utils/eth';
import { usePrice } from 'modules/wallet/price.context';
import { View, BodyText, Header, SecondaryText, Box, Row } from 'modules/styled';
import CoinStat from 'modules/stats/coinStat.component';
import { CASHOUT_MAX } from '@r3l/common';
import Tooltip from 'modules/tooltip/tooltip.component';
import WalletLinks from './walletLinks';
import { NextUnlock } from './nextUnlock';

export function Balance() {
  const user = useSelector((state) => state.auth.user);
  const screenSize = useSelector((state) => state.navigation.screenSize);

  const maxUSD = usePrice(CASHOUT_MAX);

  if (!user) return null;
  const metaMaskTokens = user.tokenBalance || 0;
  const fullMetamaskTokens = user.fullTokenBalance || 0;

  const { airdropTokens, lockedTokens } = user;
  const stakingPower = user.balance
    ? Math.round(100 * (1 - lockedTokens / (metaMaskTokens + user.balance)))
    : 0;

  const unclaimed = user.balance - user.airdropTokens;

  const accountDetail = getAccountDetail({
    unclaimed,
    metaMaskTokens,
    fullMetamaskTokens,
    getAccountDetail,
    airdropTokens,
    lockedTokens,
    stakingPower,
    stakedTokens: user.stakedTokens,
    pendingCashout: user.pendingCashout,
  });

  return (
    <View sx={{ mx: [2, 4], mt: [2, 0] }}>
      {!screenSize ? (
        <View>
          <BodyText sx={{ mt: 2 }}>
            These are coins you earned as rewards. You can transfer up to {CASHOUT_MAX}
            {maxUSD} coins to your Ethereum wallet (this limit will be increased as the
            network grows).
          </BodyText>
        </View>
      ) : null}
      <View
        sx={{
          variant: 'borders.navBorder',
          borderWidth: 1,
          borderBottomWidth: 0,
          p: 2,
          mt: 2,
        }}
      >
        <Row sx={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <BodyText sx={{ mb: 4.001 }}>Account Balance</BodyText>
          <SecondaryText>{truncateAddress(user.ethAddress[0])}</SecondaryText>
        </Row>
        <Row sx={{ alignItems: 'center', mt: 2 }}>
          <CoinStat fs={4.5} lh={5} size={7} user={user} align="center" showPrice />
        </Row>
        <Box sx={{ mt: 2 }} />
        <NextUnlock />
      </View>

      <Row sx={{ flexWrap: 'wrap', variant: 'borders.navBorder', borderWidth: 1, p: 2 }}>
        {accountDetail.map(
          (detail) =>
            (!!detail.value || detail.alwayShow) && (
              <View key={detail.text}>
                {detail.tip && (
                  <Tooltip
                    name={detail.text.replace(' ', '')}
                    data={{ text: detail.tip }}
                  />
                )}
                <SecondaryText sx={{ mr: 2 }}>
                  {detail.text}: {abbreviateNumber(detail.value)}
                </SecondaryText>
              </View>
            )
        )}
      </Row>
      <WalletLinks />
      <Box sx={{ mt: 2 }}></Box>
      <Box sx={{ mt: [4, 9] }}></Box>
      <Header>Recent Activity</Header>
    </View>
  );
}

function getAccountDetail({
  unclaimed,
  metaMaskTokens,
  fullMetamaskTokens,
  airdropTokens,
  lockedTokens,
  stakingPower,
  stakedTokens,
  pendingCashout,
}) {
  return [
    {
      text: 'Unclaimed Coins',
      value: Math.max(unclaimed, 0),
      tip: 'These are your in-app coins. You can use them for betting on posts.',
    },
    {
      text: 'Staked REL Balance',
      value: stakedTokens,
      tip: 'sRel Balance',
    },
    {
      text: 'Metamask Coins',
      value: metaMaskTokens,
      tip: 'These are the coins located in your connected Ethereum wallet and can be used for betting.',
    },
    {
      text: 'All Metamask Coins',
      value: fullMetamaskTokens,
      tip: 'Total coins in your Ethereum wallet. New transfers will be available for betting after 3 days.',
    },
    {
      text: 'Pending Cashout',
      value: pendingCashout,
    },
    {
      text: 'Airdrop Coins',
      value: airdropTokens,
      tip: 'These are coins you got for referrals and verifying social accounts.\nYou cannot transfer these coins to Metamask.',
      alwaysShow: true,
    },
    {
      text: 'Locked Coins',
      value: lockedTokens,
      tip: 'These are coins that you are currently betting on posts.\nThey get unlocked once the bets expire.',
    },
    {
      text: 'Staking Power',
      value: stakingPower + '%',
      tip: 'This is the ratio between unlocked and locked coins.',
      alwaysShow: true,
      stringValue: true,
    },
  ];
}

export default memo(Balance);
